import React, { useState } from 'react';

// ── Graticule helpers ──────────────────────────────────────────────────────────

function getGraticuleInterval(zoom) {
  if (zoom >= 14) return 0.005;
  if (zoom >= 12) return 0.01;
  if (zoom >= 10) return 0.05;
  if (zoom >= 8) return 0.1;
  if (zoom >= 6) return 0.5;
  if (zoom >= 4) return 1;
  if (zoom >= 2) return 5;
  return 10;
}

function formatDeg(value, isLng) {
  const abs = Math.abs(value);
  const dir = isLng ? (value >= 0 ? 'E' : 'W') : value >= 0 ? 'N' : 'S';
  return `${abs.toFixed(2)}\u00b0${dir}`;
}

function buildGraticuleSvg(map) {
  const bounds = map.getBounds();
  const zoom = map.getZoom();
  const interval = getGraticuleInterval(zoom);
  const container = map.getContainer();
  const W = container.offsetWidth;
  const H = container.offsetHeight;

  const west = bounds.getWest();
  const east = bounds.getEast();
  const north = bounds.getNorth();
  const south = bounds.getSouth();

  const lineStroke = 'rgba(255,255,255,0.55)';
  const strokeWidth = 0.6;
  const dashArray = '5,4';
  const fontSize = 9;

  const lines = [];
  const labels = [];

  // Meridians (constant longitude)
  const startLng = Math.ceil(west / interval) * interval;
  for (let lng = startLng; lng <= east + 1e-9; lng += interval) {
    const p1 = map.project([lng, north]);
    const p2 = map.project([lng, south]);
    lines.push(
      `<line x1="${p1.x.toFixed(1)}" y1="0" x2="${p2.x.toFixed(1)}" y2="${H}"` +
        ` stroke="${lineStroke}" stroke-width="${strokeWidth}" stroke-dasharray="${dashArray}"/>`
    );
    const labelX = ((p1.x + p2.x) / 2).toFixed(1);
    labels.push(
      `<text x="${labelX}" y="${H - 4}" text-anchor="middle"` +
        ` font-size="${fontSize}" fill="white" stroke="rgba(0,0,0,0.75)"` +
        ` stroke-width="2.5" paint-order="stroke"` +
        ` font-family="Arial,sans-serif">${formatDeg(lng, true)}</text>`
    );
  }

  // Parallels (constant latitude)
  const startLat = Math.ceil(south / interval) * interval;
  for (let lat = startLat; lat <= north + 1e-9; lat += interval) {
    const p1 = map.project([west, lat]);
    const p2 = map.project([east, lat]);
    lines.push(
      `<line x1="0" y1="${p1.y.toFixed(1)}" x2="${W}" y2="${p2.y.toFixed(1)}"` +
        ` stroke="${lineStroke}" stroke-width="${strokeWidth}" stroke-dasharray="${dashArray}"/>`
    );
    labels.push(
      `<text x="4" y="${(p1.y - 3).toFixed(1)}" font-size="${fontSize}"` +
        ` fill="white" stroke="rgba(0,0,0,0.75)" stroke-width="2.5" paint-order="stroke"` +
        ` font-family="Arial,sans-serif">${formatDeg(lat, false)}</text>`
    );
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" ` +
    `width="${W}" height="${H}" style="position:absolute;top:0;left:0;pointer-events:none;">` +
    lines.join('') +
    labels.join('') +
    '</svg>'
  );
}

// ── Scale helpers ──────────────────────────────────────────────────────────────

const EARTH_CIRCUMFERENCE = 40075016.686; // metres
const TILE_SIZE = 512; // Mapbox GL uses 512-px tiles
const SCREEN_DPI = 96;
const PX_PER_METRE = SCREEN_DPI / 0.0254; // 3779.5 px/m on a 96-dpi screen

function metersPerLogicalPx(zoom, latDeg) {
  const latRad = (latDeg * Math.PI) / 180;
  return (
    (EARTH_CIRCUMFERENCE * Math.cos(latRad)) / (TILE_SIZE * Math.pow(2, zoom))
  );
}

const STANDARD_SCALES = [
  100,
  200,
  250,
  500,
  1000,
  2000,
  2500,
  5000,
  10000,
  20000,
  25000,
  50000,
  100000,
  250000,
  500000,
  1000000
];

function snapToStandardScale(ratio) {
  return STANDARD_SCALES.reduce((prev, curr) =>
    Math.abs(curr - ratio) < Math.abs(prev - ratio) ? curr : prev
  );
}

const SCALE_BAR_LENGTHS_M = [
  10,
  20,
  50,
  100,
  200,
  500,
  1000,
  2000,
  5000,
  10000,
  20000,
  50000
];

function pickScaleBarMeters(scaleRatio) {
  // Target a ~90 px scale bar at 96 dpi
  const targetPx = 90;
  const idealMeters = (targetPx / PX_PER_METRE) * scaleRatio;
  return SCALE_BAR_LENGTHS_M.reduce((prev, curr) =>
    Math.abs(curr - idealMeters) < Math.abs(prev - idealMeters) ? curr : prev
  );
}

function formatMeters(m) {
  return m >= 1000 ? `${m / 1000} km` : `${m} m`;
}

function getScaleInfo(map) {
  const center = map.getCenter();
  const zoom = map.getZoom();
  const mpp = metersPerLogicalPx(zoom, center.lat);

  // 1 logical screen pixel = mpp metres real-world
  // At 96 dpi: 1 screen pixel = 1/PX_PER_METRE metres on paper
  // Scale ratio = mpp / (1/PX_PER_METRE) = mpp * PX_PER_METRE
  const rawRatio = mpp * PX_PER_METRE;
  const scaleRatio = snapToStandardScale(rawRatio);

  const scaleBarMeters = pickScaleBarMeters(scaleRatio);
  // Width in logical pixels for this distance at the snapped scale
  const scaleBarPx = (scaleBarMeters / scaleRatio) * PX_PER_METRE;

  return { scaleRatio, scaleBarMeters, scaleBarPx };
}

// ── North-arrow SVG ───────────────────────────────────────────────────────────

const NORTH_ARROW_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 70" width="60" height="70">
  <!-- Shadow -->
  <circle cx="30" cy="33" r="22" fill="rgba(0,0,0,0.15)"/>
  <!-- White background disc -->
  <circle cx="30" cy="33" r="20" fill="white" stroke="#555" stroke-width="1"/>
  <!-- North (red) pointer -->
  <polygon points="30,10 36,33 30,29 24,33" fill="#c0392b"/>
  <!-- South (dark) pointer -->
  <polygon points="30,56 36,33 30,37 24,33" fill="#2c3e50"/>
  <!-- Centre dot -->
  <circle cx="30" cy="33" r="3" fill="white" stroke="#555" stroke-width="1"/>
  <!-- N label -->
  <text x="30" y="8" text-anchor="middle" font-size="11" font-weight="bold"
        font-family="Arial,sans-serif" fill="#c0392b">N</text>
</svg>`;

// ── Legend helpers ────────────────────────────────────────────────────────────

// Representative legend entries for the three core layer types
const CORE_LAYER_ICONS = {
  Observations: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
    <circle cx="8" cy="8" r="6" fill="#4CAF50" stroke="white" stroke-width="1.5"/>
  </svg>`,
  Tracks: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="8">
    <line x1="0" y1="4" x2="16" y2="4" stroke="#3F51B5" stroke-width="2.5"/>
    <polygon points="12,1 16,4 12,7" fill="#3F51B5"/>
  </svg>`,
  Agents: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
    <circle cx="8" cy="8" r="7" fill="#1f4ba8" stroke="white" stroke-width="1.5"/>
  </svg>`
};

function buildLegendRows(selectedLayers, mapLayers) {
  const rows = [];

  // Core layers (Observations, Tracks, Agents)
  ['Observations', 'Tracks', 'Agents'].forEach(name => {
    if (selectedLayers.includes(name)) {
      rows.push({ name, icon: CORE_LAYER_ICONS[name] || '' });
    }
  });

  // Custom layers (everything that isn't a core layer)
  if (Array.isArray(mapLayers)) {
    mapLayers
      .filter(
        l =>
          l.projectId !== '123456' &&
          l.name !== 'Observations' &&
          l.name !== 'Tracks' &&
          l.name !== 'Agents' &&
          selectedLayers.includes(l.name)
      )
      .forEach(l => {
        const color = l.color || l.focusColor || '#888';
        const icon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
          <rect x="1" y="1" width="14" height="14" fill="${color}" rx="2"/>
        </svg>`;
        rows.push({ name: l.name, icon });
      });
  }

  return rows;
}

// ── Main print function ───────────────────────────────────────────────────────

function doPrint(map, selectedLayers, mapLayers, currentUser) {
  // 1. Capture map canvas
  const canvas = map.getCanvas();
  const mapImageDataUrl = canvas.toDataURL('image/png');

  // 2. Container logical width (height not needed; map fills remaining space)
  const container = map.getContainer();
  const W = container.offsetWidth;
  const H = container.offsetHeight;

  // 3. Graticule SVG (uses map.project → logical px coords)
  const graticuleSvg = buildGraticuleSvg(map);

  // 4. Scale
  const { scaleRatio, scaleBarMeters, scaleBarPx } = getScaleInfo(map);
  const scaleBarPxClamped = Math.max(20, Math.min(scaleBarPx, W * 0.25));

  // 5. Legend
  const legendRows = buildLegendRows(selectedLayers, mapLayers);

  // 6. Timestamp
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // 7. Legend HTML
  const legendHtml = legendRows.length
    ? `<div class="legend">
        <div class="legend-title">Legend</div>
        ${legendRows
          .map(
            r =>
              `<div class="legend-row">
                <span class="legend-icon">${r.icon}</span>
                <span class="legend-label">${r.name}</span>
              </div>`
          )
          .join('')}
      </div>`
    : '';

  // 8. Build and open print window
  const printHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Map</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { width: 100%; height: 100%; background: #fff; font-family: Arial, sans-serif; }

    .page {
      width: 100%;
      height: 100vh;
      display: flex;
      flex-direction: column;
      background: #fff;
    }

    .map-wrapper {
      width: 100%;
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
    }

    .map-inner {
      position: relative;
      max-width: 100%;
      max-height: 100%;
      aspect-ratio: ${W} / ${H};
    }

    .map-img {
      display: block;
      width: 100%;
      height: 100%;
    }

    .graticule-layer {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    .graticule-layer svg {
      width: 100% !important;
      height: 100% !important;
    }

    .north-arrow {
      position: absolute;
      top: 10px;
      left: 12px;
      width: 60px;
      height: 70px;
    }

    .scale-bar-wrap {
      position: absolute;
      bottom: 12px;
      right: 12px;
      background: rgba(255,255,255,0.92);
      border: 1px solid #555;
      border-radius: 3px;
      padding: 3px 6px 4px;
      font-size: 10px;
      font-family: Arial, sans-serif;
      text-align: center;
    }
    .scale-ratio { font-size: 9px; color: #555; margin-bottom: 2px; }
    .scale-bar-outer {
      position: relative;
      height: 10px;
      width: ${scaleBarPxClamped.toFixed(0)}px;
      border: 1.5px solid #333;
      border-top: none;
      margin-bottom: 1px;
      display: flex;
    }
    .scale-bar-half {
      flex: 1;
      height: 100%;
    }
    .scale-bar-half:nth-child(odd)  { background: #333; }
    .scale-bar-half:nth-child(even) { background: #fff; }
    .scale-bar-label {
      font-size: 9px;
      display: flex;
      justify-content: space-between;
    }

    .legend {
      position: absolute;
      bottom: 12px;
      left: 12px;
      background: rgba(255,255,255,0.92);
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 5px 8px;
      font-size: 10px;
      font-family: Arial, sans-serif;
      max-width: 160px;
    }
    .legend-title {
      font-weight: bold;
      font-size: 10px;
      margin-bottom: 4px;
      border-bottom: 1px solid #ddd;
      padding-bottom: 2px;
    }
    .legend-row {
      display: flex;
      align-items: center;
      gap: 5px;
      margin-bottom: 3px;
    }
    .legend-icon { flex-shrink: 0; display: flex; align-items: center; }
    .legend-label { font-size: 10px; }

    .footer {
      background: #f5f5f5;
      border-top: 1px solid #ccc;
      padding: 4px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 9px;
      color: #444;
      flex-shrink: 0;
    }

    @page {
      size: A4 landscape;
      margin: 0;
    }
    @media print {
      html, body { width: 100%; height: 100%; overflow: hidden; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="map-wrapper">
      <div class="map-inner">
        <img class="map-img" src="${mapImageDataUrl}" alt="Map"/>

        <!-- Lat/lon graticule overlay -->
        <div class="graticule-layer">
          ${graticuleSvg}
        </div>

        <!-- North arrow -->
        <div class="north-arrow">${NORTH_ARROW_SVG}</div>

        <!-- Scale bar -->
        <div class="scale-bar-wrap">
          <div class="scale-ratio">1 : ${scaleRatio.toLocaleString()}</div>
          <div class="scale-bar-outer">
            <div class="scale-bar-half"></div>
            <div class="scale-bar-half"></div>
            <div class="scale-bar-half"></div>
            <div class="scale-bar-half"></div>
          </div>
          <div class="scale-bar-label">
            <span>0</span>
            <span>${formatMeters(scaleBarMeters)}</span>
          </div>
        </div>

        <!-- Legend -->
        ${legendHtml}
      </div>
    </div>

    <div class="footer">
      <span>Powered by Sensing Clues</span>
      <span>
        Printed by ${currentUser ||
          'unknown'} &nbsp;|&nbsp; ${dateStr} ${timeStr}
      </span>
    </div>
  </div>

  <script>
    window.addEventListener('load', function () {
      window.print();
    });
  </script>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // eslint-disable-next-line no-alert
    alert(
      'Could not open print window. Please allow pop-ups for this page and try again.'
    );
    return;
  }
  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();
}

// ── React component ───────────────────────────────────────────────────────────

function MapPrint({ map, selectedLayers, mapLayers, currentUser }) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    if (!map || isPrinting) return;
    setIsPrinting(true);
    // Wait for any in-progress render to finish before capturing the canvas
    map.once('idle', () => {
      try {
        doPrint(map, selectedLayers || [], mapLayers || [], currentUser);
      } finally {
        setIsPrinting(false);
      }
    });
    map.triggerRepaint();
  };

  return (
    <div id="print-map-menu" className="dropdown dropleft">
      <button
        className="svg-button"
        type="button"
        onClick={handlePrint}
        disabled={isPrinting}
        title="Print map"
        aria-label="Print map"
      >
        <span className="material-icons print-map-icon" aria-hidden="true">
          print
        </span>
      </button>
    </div>
  );
}

export default MapPrint;
